import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import LoadingPage from '../../LoadingPage';
import StoreManagement from '../StoreManagement/StoreManagement';
import './ControlPanel.css';

// Basic App interface - flexible to match AppsPage
interface App {
  id: string;
  name: string;
  type?: string;
  region?: string;
  organizationId: string;
  appIcon?: string;
  status?: string;
}

// Store interface
interface Store {
  id?: string;
  name: string;
  city: string;
  country: string;
  stripeConnected: boolean;
}

// Deployment status interface
interface DeploymentStatus {
  projectId?: string;
  status?: string;
  details?: string;
  timestamp?: string;
  url?: string;
  currentStep?: string;
  requestId?: string;
  error?: string;
}

interface ControlPanelProps {
  db: Firestore;
  functions: Functions;
  appData: App;
  organizationId: string;
  onBack: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  db,
  functions,
  appData,
  organizationId,
  onBack,
  onError,
  onSuccess,
  setLoading
}) => {
  const { currentUser } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [showStoreManagement, setShowStoreManagement] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [retryingDeployment, setRetryingDeployment] = useState(false);

  // Load stores for this app
  useEffect(() => {
    const loadStores = async () => {
      if (!appData?.id) {
        setStoresLoading(false);
        return;
      }

      setStoresLoading(true);
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appData.id)
        );
        const querySnapshot = await getDocs(q);
        
        const storesList: Store[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storesList.push({
            id: doc.id,
            name: data.name || 'Untitled Store',
            city: data.city || 'Unknown City',
            country: data.country || 'Unknown Country',
            stripeConnected: data.stripeConnected || false
          });
        });

        setStores(storesList);
      } catch (err) {
        console.error('Error loading stores:', err);
        onError('Failed to load stores.');
      } finally {
        setStoresLoading(false);
      }
    };

    loadStores();
  }, [db, appData?.id, onError]);

  // Load deployment status
  useEffect(() => {
    if (appData?.id) {
      checkDeploymentStatus();
    }
  }, [appData?.id]);

  const checkDeploymentStatus = async () => {
    if (!appData?.id) return;

    setDeploymentLoading(true);
    try {
      const checkStatus = httpsCallable(functions, 'checkInstanceDeploymentStatus');
      const result = await checkStatus({ appId: appData.id });
      setDeploymentStatus(result.data as DeploymentStatus);
    } catch (err: any) {
      console.error('Error checking deployment status:', err);
      // Don't show error to user, just log it
      setDeploymentStatus(null);
    } finally {
      setDeploymentLoading(false);
    }
  };

  const handleRetryDeployment = async () => {
    if (!appData?.id) return;

    setRetryingDeployment(true);
    try {
      const retryDeployment = httpsCallable(functions, 'retryInstanceDeployment');
      await retryDeployment({ appId: appData.id });
      onSuccess('Deployment retry initiated successfully');
      
      // Wait a bit then check status
      setTimeout(() => {
        checkDeploymentStatus();
      }, 2000);
    } catch (err: any) {
      console.error('Error retrying deployment:', err);
      onError(err.message || 'Failed to retry deployment');
    } finally {
      setRetryingDeployment(false);
    }
  };

  const handleManageStores = () => {
    setShowStoreManagement(true);
  };

  const handleBackFromStoreManagement = () => {
    setShowStoreManagement(false);
    const reloadStores = async () => {
      if (!appData?.id) return;

      setStoresLoading(true);
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appData.id)
        );
        const querySnapshot = await getDocs(q);
        
        const storesList: Store[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storesList.push({
            id: doc.id,
            name: data.name || 'Untitled Store',
            city: data.city || 'Unknown City',
            country: data.country || 'Unknown Country',
            stripeConnected: data.stripeConnected || false
          });
        });

        setStores(storesList);
      } catch (err) {
        console.error('Error reloading stores:', err);
        onError('Failed to reload stores.');
      } finally {
        setStoresLoading(false);
      }
    };

    reloadStores();
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'unknown';
    if (status === 'completed') return 'success';
    if (status === 'failed' || status === 'error') return 'error';
    if (status === 'in-progress' || status === 'pending') return 'progress';
    return 'unknown';
  };

  // Show store management if selected
  if (showStoreManagement) {
    return (
      <StoreManagement
        db={db}
        functions={functions}
        appData={appData}
        selectedOrganizationId={organizationId}
        onBack={handleBackFromStoreManagement}
        onBackToOrganizations={onBack}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
      />
    );
  }

  return (
    <div className="control-panel">
      {/* Header */}
      <div className="control-panel-header">
        <button className="control-panel-back-btn" onClick={onBack}>
          ← Back to Sites
        </button>
        <h1>Control Panel: {appData.name}</h1>
      </div>

      {/* Content */}
      <div className="control-panel-content">

        {/* App Information Card */}
        <div className="control-panel-card">
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h4>App Information</h4>
          </div>
          
          <div className="control-panel-card-content">
            <div className="info-grid">
              <div className="info-item">
                <label>App ID:</label>
                <span>{appData.id}</span>
              </div>
              <div className="info-item">
                <label>Name:</label>
                <span>{appData.name}</span>
              </div>
              <div className="info-item">
                <label>Type:</label>
                <span>{appData.type || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>Region:</label>
                <span>{appData.region || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>Organization:</label>
                <span>{organizationId}</span>
              </div>
              {/* <div className="info-item">
                <label>Status:</label>
                <span className="status-badge">
                  {appData.status || 'Unknown'}
                </span>
              </div> */}
            </div>
          </div>
        </div>

        {/* Store Management Card */}
        <div className={`control-panel-card ${stores.length === 0 ? 'incomplete' : ''}`}>
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9,22 9,12 15,12 15,22"/>
              </svg>
            </div>
            <h4>Store Management</h4>
            {!storesLoading && (
              <span className={`store-count-badge ${stores.length === 0 ? 'empty' : 'has-stores'}`}>
                {stores.length}
              </span>
            )}
          </div>
          
          <div className="control-panel-card-content">
            {storesLoading ? (
              <div className="control-panel-loading">
                <div className="loading-spinner"></div>
                <p>Loading stores...</p>
              </div>
            ) : stores.length === 0 ? (
              <div className="no-stores">
                <span className="warning-icon">⚠️</span>
                <p>No stores added yet. Your app needs at least one store to accept orders.</p>
              </div>
            ) : (
              <div className="stores-preview">
                {stores.slice(0, 3).map((store) => (
                  <div key={store.id} className="store-preview-item">
                    <div className="store-preview-info">
                      <h6>{store.name}</h6>
                      <p>{store.city}, {store.country}</p>
                    </div>
                    <div className="store-preview-status">
                      <span className={`status-dot ${store.stripeConnected ? 'connected' : 'pending'}`}></span>
                      <span className="status-text">
                        {store.stripeConnected ? 'Active' : 'Setup Pending'}
                      </span>
                    </div>
                  </div>
                ))}
                {stores.length > 3 && (
                  <div className="more-stores-indicator">
                    +{stores.length - 3} more store{stores.length - 3 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
            
            <div className="stores-actions">
              <button 
                type="button"
                className={`manage-stores-btn ${stores.length === 0 ? 'primary' : 'secondary'}`}
                onClick={handleManageStores}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {stores.length === 0 ? (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </>
                  ) : (
                    <>
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                      <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                      <path d="M13 12h3a2 2 0 0 1 2 2v1"/>
                      <path d="M11 12H8a2 2 0 0 0-2 2v1"/>
                    </>
                  )}
                </svg>
                {stores.length === 0 ? 'Add Your First Store' : 'Manage Stores'}
              </button>
            </div>
          </div>
        </div>

                {/* Deployment Status Card */}
        
        {stores.length > 0 && <div className={`control-panel-card ${deploymentStatus?.error ? 'incomplete' : ''}`}>
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h4>Deployment Status</h4>
          </div>
          
          <div className="control-panel-card-content">
            {deploymentLoading ? (
              <div className="control-panel-loading">
                <div className="loading-spinner"></div>
                <p>Checking status...</p>
              </div>
            ) : deploymentStatus ? (
              <>
                {deploymentStatus.error ? (
                  <div className="deployment-error">
                    <span className="error-icon">❌</span>
                    <div className="error-details">
                      <p className="error-message">{deploymentStatus.error}</p>
                      {deploymentStatus.requestId && (
                        <p className="request-id">Request ID: {deploymentStatus.requestId}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="deployment-info">
                    <div className="deployment-status-row">
                      <label>Status:</label>
                      <span className={`deployment-status-badge ${getStatusColor(deploymentStatus.status)}`}>
                        {deploymentStatus.status || 'Unknown'}
                      </span>
                    </div>
                    
                    {deploymentStatus.currentStep && (
                      <div className="deployment-status-row">
                        <label>Current Step:</label>
                        <span>{deploymentStatus.currentStep}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.details && (
                      <div className="deployment-status-row">
                        <label>Details:</label>
                        <span>{deploymentStatus.details}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.url && (
                      <div className="deployment-status-row">
                        <label>URL:</label>
                        <a href={deploymentStatus.url} target="_blank" rel="noopener noreferrer" className="deployment-url">
                          {deploymentStatus.url}
                        </a>
                      </div>
                    )}
                    
                    {deploymentStatus.timestamp && (
                      <div className="deployment-status-row">
                        <label>Last Updated:</label>
                        <span>{new Date(deploymentStatus.timestamp).toLocaleString()}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.requestId && (
                      <div className="deployment-status-row">
                        <label>Request ID:</label>
                        <span className="request-id-text">{deploymentStatus.requestId}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="deployment-actions">
                  <button 
                    type="button"
                    className="refresh-status-btn"
                    onClick={checkDeploymentStatus}
                    disabled={deploymentLoading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10"/>
                      <polyline points="1 20 1 14 7 14"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                    Refresh Status
                  </button>
                  
                  {deploymentStatus.error && (
                    <button 
                      type="button"
                      className="retry-deployment-btn"
                      onClick={handleRetryDeployment}
                      disabled={retryingDeployment}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      {retryingDeployment ? 'Retrying...' : 'Retry Deployment'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="no-deployment-status">
                <p>No deployment status available</p>
                <button 
                  type="button"
                  className="refresh-status-btn"
                  onClick={checkDeploymentStatus}
                  disabled={deploymentLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  Check Status
                </button>
              </div>
            )}
          </div>
        </div>

        }

      </div>
    </div>
  );
};

export default ControlPanel;